// @ts-strict-ignore
import type { InMemoryCache } from "@apollo/client";
import { useTranslation } from "react-i18next";

import {
  Button,
  Modal,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  Typography,
  useToast,
} from "@aviary";
import { l } from "@emerson/locales/i18n";

import { useCartDeleteMutation, type CartDeleteData } from "./CartDelete.mutation";

import * as styles from "./DeleteCartModal.styles";

interface Props {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
}

const DeleteCartModal = ({ isOpen, orderId, onClose }: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();

  const [cartDelete, { loading }] = useCartDeleteMutation({
    variables: {
      input: {
        orderId,
      },
    },
    update: (cache: InMemoryCache) => {
      const cachedCart = cache.identify({
        id: orderId,
        __typename: "WholesaleOrder",
      });

      if (cache.evict({ id: cachedCart })) {
        cache.gc();

        cache.modify({
          fields: {
            internalWholesaleCarts: (existingCartRefs = [], { readField }) => {
              return existingCartRefs.filter(cartRef => orderId !== readField("id", cartRef));
            },
          },
        });
      }
    },
    onCompleted: data => handleCartDeleteMutationComplete(data),
    onError: () => handleError(),
  });

  const handleCartDeleteMutationComplete = (data: CartDeleteData) => {
    if (data.cartDelete?.errors.length) {
      return handleError();
    }
    makeToast("success", t(l.cart.CartDeleteSuccess));
    onClose();
  };

  const handleError = () => {
    makeToast("error", t(l.cart.CartDeleteFailure));
    onClose();
  };

  return (
    <Modal size="small" isOpen={isOpen} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <Typography type="h4">{t(l.cart.RemoveCartHeading)}</Typography>
        </ModalHeader>
        <Typography>{t(l.cart.ConfirmDeleteCart)}</Typography>
        <ModalFooter css={styles.modalFooter}>
          <Button css={styles.cancelButton} onClick={onClose} isColor="system">
            {t(l.common.Cancel)}
          </Button>
          <Button onClick={() => cartDelete()} isLoading={loading} aria-label={t(l.common.Delete)}>
            {t(l.common.Delete)}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export { DeleteCartModal };
